import type { Candidate } from '../candidate'
import type { DerivedFacts } from '../facts'
import { RULE_IDS } from '../version'
import { evidenceFor, missingEvidenceFor, watchItemsFor } from './shared'

/**
 * The fat-loss family, complete from Checkpoint 2.4.
 *
 * Three documented rules were deliberately absent from 1.7 to 2.3, because the
 * evidence they read was not collected and D-060 forbids implementing a rule
 * whose evidence the app does not gather. All three now exist:
 *
 *  - **Plateau** — weight and waist flat with adherence at the tier D-066
 *    requires. Implemented below as `plateau`, now that adherence is recorded.
 *  - **Improve adherence first** — implemented once, in `shared.ts`, because it
 *    applies in every phase rather than only this one.
 *  - **Loss faster than range PLUS declining strength or recovery** — the
 *    strength and recovery halves are now their own cross-phase families in
 *    `shared.ts`, and the selector combines them with the rate rule here using
 *    the approved precedence (D-066) rather than one rule trying to weigh both.
 *
 * Splitting the last of those was the significant choice: recovery and strength
 * decline are reasons to ease ANY phase, not facts about fat loss, and folding
 * them in here would have meant writing them four more times.
 */
export function evaluateFatLoss(facts: DerivedFacts, adherenceIsHigh: boolean): Candidate {
  const phase = facts.phase

  // The engine only dispatches with a phase, and only when the week is
  // comparable; both are guaranteed upstream.
  if (!phase || phase.ratePosition === undefined) {
    throw new Error('fat-loss rules require a phase and a comparable week')
  }

  if (phase.ratePosition === 'insideRange') return stayTheCourse(facts)
  if (phase.ratePosition === 'fasterThanRange') return lossFasterThanRange(facts)
  if (facts.weight.isFlat === true && facts.waist.direction === 'falling') {
    return stableWeightFallingWaist(facts)
  }

  /* The plateau rule, deferred since Checkpoint 1.7 and implementable now that
     adherence is collected (D-060).
     ─────────────────────────────────────────────────────────────────────────
     Weight and waist both flat, with adherence at the tier D-066 requires
     before ANY adjustment is proposed. Without that tier the honest answer is
     still that the plan has not been tested — which is the branch below, and is
     what D-007 demands. */
  const bothFlat = facts.weight.isFlat === true && facts.waist.direction !== 'falling'
  if (bothFlat && adherenceIsHigh) return plateau(facts)

  return offTargetWithoutAdherenceEvidence(facts)
}

/**
 * A genuine plateau: weight and waist flat, and the plan demonstrably followed.
 *
 * This is the one fat-loss rule that proposes eating less, so
 * `reducesIntakeOrActivity` is **true** and D-007's filter still guards it — a
 * belt-and-braces arrangement that is load-bearing rather than redundant,
 * because the filter also protects against a future ruleset forgetting.
 *
 * The documented adjustment sizes — about 100–150 kcal, or 1,500–2,000 steps —
 * are **deliberately absent from the action text**. The handoff calls them
 * examples to be reviewed for the athlete, and `docs/decisions.md` records that
 * suggested sizes "must not become hidden universal constants". One lever is
 * named; the size is left to the user.
 */
function plateau(facts: DerivedFacts): Candidate {
  return {
    category: 'considerOneSmallAdjustment',
    primaryAction:
      'Make one small change — either eat a little less or add some daily steps, not both. Weight and waist have both been flat while you followed the plan closely, which is what a real plateau looks like.',
    supportingActions: [],
    ruleId: RULE_IDS.fatLossPlateau,
    family: 'phaseProgress',
    leadingEvidence: 'waist',
    evidence: evidenceFor(facts),
    watchItems: [
      'Change one thing at a time, and give it two or three weeks before judging it.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Change intake and activity together',
        reason:
          'Changing two things at once makes it impossible to tell which one worked, and this phase judges a change over the following weeks.',
      },
      {
        action: 'Make a large reduction to break the plateau quickly',
        reason:
          'A larger deficit is where strength and recovery suffer. One small change is enough to tell whether the plateau is real.',
      },
    ],
    reducesIntakeOrActivity: true,
  }
}

/** Loss is inside the range the user configured for this phase. */
function stayTheCourse(facts: DerivedFacts): Candidate {
  return {
    category: 'stayTheCourse',
    primaryAction:
      'Keep this week exactly as it was. Your rate of loss is inside the range you set for this phase.',
    supportingActions: [],
    ruleId: RULE_IDS.fatLossInRange,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: watchItemsFor(facts),
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Reduce food intake',
        reason:
          'Progress is already inside your configured range, so there is nothing for a reduction to fix.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Loss is faster than the user's own configured maximum.
 *
 * This is the one rule that recommends a change, and it is deliberately the
 * conservative direction: easing a deficit. It rests entirely on a boundary the
 * user configured during setup, so it invents no coaching threshold — the app
 * is telling them they are outside the range they themselves set. The
 * documented rule also weighs strength and recovery decline; that evidence does
 * not exist yet, which is why it is listed as missing and why confidence cannot
 * reach its top level.
 */
function lossFasterThanRange(facts: DerivedFacts): Candidate {
  return {
    category: 'easeTheCurrentPhase',
    primaryAction:
      'Ease the deficit slightly so your weekly loss comes back inside the range you set for this phase.',
    supportingActions: [
      'Prioritise sleep and protein this week to protect muscle while the rate settles.',
    ],
    ruleId: RULE_IDS.fatLossFasterThanRange,
    family: 'safetyAndRecovery',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Losing faster than planned is where strength and recovery usually suffer first.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: [
      'Sentinel-lift performance, which would show whether strength is holding',
      'Recovery ratings for sleep, energy, hunger and training performance',
      ...missingEvidenceFor(facts),
    ],
    rejectedAlternatives: [
      {
        action: 'Carry on at the current rate',
        reason:
          'The rate is outside the range you configured, and faster loss is where muscle and recovery are most at risk.',
      },
      {
        action: 'Reduce intake further',
        reason: 'Loss is already faster than planned; a larger deficit would push it further out.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Weight is flat but the waist is falling.
 *
 * The explicitly documented case for NOT cutting calories: waist leads the
 * evidence hierarchy (D-005), and a falling waist with flat weight is progress
 * that the scale is not showing.
 */
function stableWeightFallingWaist(facts: DerivedFacts): Candidate {
  return {
    category: 'doNotAdjustYet',
    primaryAction:
      'Change nothing this week. Your weight is flat but your waist is down, which is progress the scale is not showing.',
    supportingActions: [],
    ruleId: RULE_IDS.fatLossStableWeightFallingWaist,
    family: 'phaseProgress',
    leadingEvidence: 'waist',
    evidence: evidenceFor(facts),
    watchItems: [
      'If the waist stops falling as well, that is the point to look at intake again.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Reduce food intake because the scale did not move',
        reason:
          'The waist measurement is falling, which is higher-priority evidence than scale weight. Cutting now would react to the weaker signal.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Off target, and the app cannot say why without evidence it does not collect.
 *
 * The documented response — consider one small adjustment — requires high
 * adherence over 14–21 days. Guessing instead would be exactly the "recommend
 * cutting intake because low adherence made progress unclear" that D-007
 * forbids, so the app states what it would need.
 */
function offTargetWithoutAdherenceEvidence(facts: DerivedFacts): Candidate {
  return {
    category: 'gatherMoreData',
    primaryAction:
      'Keep this week as it is and keep weighing in. Project Sola cannot judge a change yet without knowing how closely you followed the plan.',
    supportingActions: [],
    ruleId: RULE_IDS.fatLossOffTargetUnjudgeable,
    family: 'adherence',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Two to three weeks of consistent data is what turns a slow week into a real trend.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: [
      'How closely you followed your plan — adherence tracking arrives in a later version',
      'Sentinel-lift performance',
      ...missingEvidenceFor(facts),
    ],
    rejectedAlternatives: [
      {
        action: 'Reduce food intake',
        reason:
          'Project Sola does not yet know how well the current plan was followed, and it will not recommend eating less on the basis of a plan that may not have been tested.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
